import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store/slices/uiSlice';
import { adminApiService } from '../../services/adminApi';
import memberService from '../../services/memberService';
import organizationService from '../../services/organizationService';
import * as XLSX from 'xlsx';

const CourseEnrollmentModal = ({ event, onClose, initialSectionId = '' }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [orgTree, setOrgTree] = useState([]);
  const [orgScope, setOrgScope] = useState('group'); // group | zone | district
  const [orgScopeId, setOrgScopeId] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [membersRes, eventDetail, tree] = await Promise.all([
          memberService.getMembers({ limit: 1000 }),
          adminApiService.events.getById(event.id),
          organizationService.getOrganizationTree().catch(() => []),
        ]);
        const eventData = eventDetail.data || eventDetail;
        const targetSectionId = initialSectionId || eventData.sections?.[0]?.id || '';
        setSections(eventData.sections || []);
        setSectionId(targetSectionId);
        setMembers(membersRes.data || []);
        setOrgTree(tree || []);
        if (targetSectionId) {
          const enrollRes = await adminApiService.events.getEnrollments(event.id, targetSectionId);
          setEnrollments(enrollRes.items || []);
        } else {
          setEnrollments([]);
        }
      } catch (err) {
        console.error('Failed to load enrollments', err);
        dispatch(addNotification({ type: 'error', message: '載入參加者名單失敗' }));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [event.id, dispatch, initialSectionId]);

  const orgOptions = useMemo(() => {
    if (orgScope === 'district') {
      return (orgTree || []).map((d) => ({ id: d.id, label: d.name }));
    }
    if (orgScope === 'zone') {
      const options = [];
      (orgTree || []).forEach((d) => {
        (d.zones || []).forEach((z) => {
          options.push({ id: z.id, label: `${z.name}（${d.name}）` });
        });
      });
      return options;
    }
    const options = [];
    (orgTree || []).forEach((d) => {
      (d.zones || []).forEach((z) => {
        (z.groups || []).forEach((g) => {
          options.push({ id: g.id, label: `${g.name}（${z.name} / ${d.name}）` });
        });
      });
    });
    return options;
  }, [orgTree, orgScope]);

  const enrolledMemberIds = useMemo(() => new Set(enrollments.map(e => e.member_id)), [enrollments]);

  const filteredMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members;
    return members.filter(m =>
      (m.chi_name || '').toLowerCase().includes(term) ||
      (m.eng_name || '').toLowerCase().includes(term) ||
      (m.worship_id || '').toLowerCase().includes(term)
    );
  }, [members, search]);

  const handleAdd = async (memberId) => {
    if (!memberId || saving || !sectionId) return;
    try {
      setSaving(true);
      const res = await adminApiService.events.addEnrollment(event.id, sectionId, memberId);
      setEnrollments(prev => enrolledMemberIds.has(memberId) ? prev : [...prev, res]);
      dispatch(addNotification({ type: 'success', message: '已加入參加者名單' }));
    } catch (err) {
      console.error('Add enrollment failed', err);
      dispatch(addNotification({ type: 'error', message: '加入參加者名單失敗' }));
    } finally {
      setSaving(false);
    }
  };

  const handleAddByOrg = async () => {
    if (!sectionId || !orgScopeId || saving) return;
    try {
      setSaving(true);
      const res = await adminApiService.events.bulkAddEnrollmentsByOrg(
        event.id,
        sectionId,
        orgScope,
        parseInt(orgScopeId, 10)
      );
      const enrollRes = await adminApiService.events.getEnrollments(event.id, sectionId);
      setEnrollments(enrollRes.items || []);
      const name = res.scope_name || '';
      dispatch(addNotification({
        type: 'success',
        message: `已加入「${name}」：新增 ${res.added} 人，已在名單跳過 ${res.skipped} 人（共匹配 ${res.total_matched} 人）`,
      }));
      setOrgScopeId('');
    } catch (err) {
      console.error('Bulk add by org failed', err);
      const detail = err?.response?.data?.detail;
      dispatch(addNotification({
        type: 'error',
        message: typeof detail === 'string' ? detail : '批量加入小組／分區／牧區失敗',
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (memberId) => {
    if (saving || !sectionId) return;
    try {
      setSaving(true);
      await adminApiService.events.removeEnrollment(event.id, sectionId, memberId);
      setEnrollments(prev => prev.filter(e => e.member_id !== memberId));
      dispatch(addNotification({ type: 'success', message: '已移除參加者名單' }));
    } catch (err) {
      console.error('Remove enrollment failed', err);
      dispatch(addNotification({ type: 'error', message: '移除參加者名單失敗' }));
    } finally {
      setSaving(false);
    }
  };

  // Export to Excel
  const handleExportToExcel = () => {
    if (!sectionId || enrollments.length === 0) {
      dispatch(addNotification({ type: 'warning', message: '沒有資料可匯出' }));
      return;
    }

    try {
      const section = sections.find(s => s.id === parseInt(sectionId));
      const sectionName = section?.section_name || `Section ${sectionId}`;
      
      // Prepare data for Excel
      const data = enrollments.map((enrollment, index) => {
        const member = members.find(m => m.id === enrollment.member_id);
        return {
          序號: index + 1,
          會員名字: member?.chi_name || '未知',
          英文名: member?.eng_name || '',
          崇拜編碼: member?.worship_id || '',
          手提電話: member?.mobile || '',
          電郵: member?.email || ''
        };
      });

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      
      // Adjust column widths
      const colWidths = [8, 15, 15, 12, 15, 20];
      ws['!cols'] = colWidths.map(w => ({ wch: w }));
      
      XLSX.utils.book_append_sheet(wb, ws, sectionName);
      
      // Download file
      XLSX.writeFile(wb, `${event.event_name}-${sectionName}-${new Date().toISOString().split('T')[0]}.xlsx`);
      
      dispatch(addNotification({ type: 'success', message: '已匯出參加者名單' }));
    } catch (err) {
      console.error('Export failed', err);
      dispatch(addNotification({ type: 'error', message: '匯出失敗' }));
    }
  };

  // Export template
  const handleExportTemplate = () => {
    try {
      const templateData = [
        {
          序號: 1,
          會員名字: '李明',
          英文名: 'Lee Ming',
          崇拜編碼: 'W001',
          手提電話: '91234567',
          電郵: 'lee@example.com'
        },
        {
          序號: 2,
          會員名字: '王小美',
          英文名: 'Wong Siu Mei',
          崇拜編碼: 'W002',
          手提電話: '92345678',
          電郵: 'wong@example.com'
        }
      ];

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(templateData);
      
      const colWidths = [8, 15, 15, 12, 15, 20];
      ws['!cols'] = colWidths.map(w => ({ wch: w }));
      
      XLSX.utils.book_append_sheet(wb, ws, '參加者名單範本');
      XLSX.writeFile(wb, `參加者名單匯入範本.xlsx`);
      
      dispatch(addNotification({ type: 'success', message: '已下載匯入範本' }));
    } catch (err) {
      console.error('Export template failed', err);
      dispatch(addNotification({ type: 'error', message: '下載範本失敗' }));
    }
  };

  // Import from Excel
  const handleImportFromExcel = (e) => {
    const file = e.target.files?.[0];
    if (!file || !sectionId) {
      dispatch(addNotification({ type: 'error', message: '請先選擇場次' }));
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const data = event.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            dispatch(addNotification({ type: 'warning', message: 'Excel 文件無資料' }));
            return;
          }

          // Find members by worship_id or mobile
          let addedCount = 0;
          let skippedCount = 0;
          const errors = [];

          setSaving(true);

          for (const row of jsonData) {
            const worshipId = row['崇拜編碼'] || row['worship_id'] || '';
            const mobile = row['手提電話'] || row['mobile'] || '';
            const chiName = row['會員名字'] || row['chi_name'] || '';

            let targetMember = null;

            // Try to find by worship_id first
            if (worshipId) {
              targetMember = members.find(m => m.worship_id === worshipId);
            }

            // If not found, try by mobile
            if (!targetMember && mobile) {
              targetMember = members.find(m => m.mobile === mobile);
            }

            // If not found, try by name (fuzzy match)
            if (!targetMember && chiName) {
              targetMember = members.find(m => m.chi_name?.includes(chiName));
            }

            if (!targetMember) {
              skippedCount++;
              errors.push(`"${chiName || worshipId || mobile}" 未找到會員`);
              continue;
            }

            // Check if already enrolled
            const alreadyEnrolled = enrollments.some(e => e.member_id === targetMember.id);
            if (alreadyEnrolled) {
              skippedCount++;
              continue;
            }

            try {
              await adminApiService.events.addEnrollment(event.id, sectionId, targetMember.id);
              addedCount++;
            } catch (addErr) {
              skippedCount++;
              errors.push(`新增 "${targetMember.chi_name}" 失敗`);
            }
          }

          setSaving(false);

          // Reload enrollments
          const enrollRes = await adminApiService.events.getEnrollments(event.id, sectionId);
          setEnrollments(enrollRes.items || []);

          let message = `成功新增 ${addedCount} 個會員`;
          if (skippedCount > 0) {
            message += `，跳過 ${skippedCount} 個`;
          }
          dispatch(addNotification({ type: 'success', message }));

          if (errors.length > 0 && errors.length <= 5) {
            dispatch(addNotification({ type: 'info', message: '跳過原因: ' + errors.join('; ') }));
          }
        } catch (parseErr) {
          console.error('Parse Excel failed', parseErr);
          dispatch(addNotification({ type: 'error', message: '解析 Excel 失敗，請確認格式正確' }));
          setSaving(false);
        }
      };
      reader.readAsBinaryString(file);

      // Reset file input
      e.target.value = '';
    } catch (err) {
      console.error('Import failed', err);
      dispatch(addNotification({ type: 'error', message: '匯入失敗' }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">{event.event_name} - 參加者名單</h2>
            <p className="text-sm text-gray-500">若已加入參加者名單，簽到時只允許名單內會員</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 gap-2">
            <div className="flex-1">
              <label className="text-sm text-gray-700">場次</label>
              <select
                className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                value={sectionId}
                onChange={async (e) => {
                  const sid = e.target.value;
                  setSectionId(sid);
                  setLoading(true);
                  try {
                    if (sid) {
                      const enrollRes = await adminApiService.events.getEnrollments(event.id, sid);
                      setEnrollments(enrollRes.items || []);
                    } else {
                      setEnrollments([]);
                    }
                  } catch (err) {
                    console.error('Load enrollments failed', err);
                    dispatch(addNotification({ type: 'error', message: '載入場次名單失敗' }));
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {sections.length === 0 && <option value="">沒有場次</option>}
                {sections.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.section_name || `Section ${s.id}`} {s.start_at ? `(${new Date(s.start_at).toLocaleString()})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Import/Export Buttons */}
          <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded border border-gray-200">
            <button
              type="button"
              onClick={handleExportToExcel}
              disabled={loading || !sectionId || enrollments.length === 0}
              className="px-3 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white text-sm rounded font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              匯出名單
            </button>

            <label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportFromExcel}
                disabled={loading || saving || !sectionId}
                className="hidden"
              />
              <span className="px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white text-sm rounded font-medium flex items-center gap-2 cursor-pointer inline-block">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9-2-9-18-9 18 9 2zm0 0V5.5M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                匯入名單
              </span>
            </label>

            <button
              type="button"
              onClick={handleExportTemplate}
              className="px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm rounded font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              下載範本
            </button>
          </div>

          <div className="p-3 bg-blue-50 rounded border border-blue-100 space-y-2">
            <div className="text-sm font-medium text-gray-800">按組織一次加入</div>
            <p className="text-xs text-gray-500 m-0">可一次加入整個小組、分區或牧區內所有有效組員（已在名單者會自動跳過）</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                className="border border-gray-300 rounded px-3 py-2 bg-white"
                value={orgScope}
                onChange={(e) => {
                  setOrgScope(e.target.value);
                  setOrgScopeId('');
                }}
                disabled={loading || saving || !sectionId}
              >
                <option value="group">小組</option>
                <option value="zone">分區</option>
                <option value="district">牧區</option>
              </select>
              <select
                className="flex-1 border border-gray-300 rounded px-3 py-2 bg-white"
                value={orgScopeId}
                onChange={(e) => setOrgScopeId(e.target.value)}
                disabled={loading || saving || !sectionId}
              >
                <option value="">
                  {orgScope === 'district' ? '選擇牧區' : orgScope === 'zone' ? '選擇分區' : '選擇小組'}
                </option>
                {orgOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddByOrg}
                disabled={loading || saving || !sectionId || !orgScopeId}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white text-sm rounded font-medium whitespace-nowrap"
              >
                加入全部
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 gap-2">
            <input
              type="text"
              placeholder="搜尋姓名或崇拜編碼"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              className="border border-gray-300 rounded px-3 py-2"
              onChange={(e) => handleAdd(parseInt(e.target.value, 10))}
              disabled={loading || saving}
              value=""
            >
              <option value="">選擇會員加入</option>
              {filteredMembers.filter(m => !enrolledMemberIds.has(m.id)).map(m => (
                <option key={m.id} value={m.id}>
                  {m.chi_name} {m.worship_id ? `(${m.worship_id})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="max-h-96 overflow-y-auto border rounded">
            {loading ? (
              <div className="p-4 text-center text-gray-500">載入中...</div>
            ) : enrollments.length === 0 ? (
              <div className="p-4 text-center text-gray-500">尚未加入任何會員</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">會員</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">崇拜編碼</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">出席狀態</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {enrollments.map(enrollment => {
                    const member = members.find(m => m.id === enrollment.member_id);
                    return (
                      <tr key={enrollment.id}>
                        <td className="px-4 py-2 text-sm text-gray-900">{member?.chi_name || '未知'}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{member?.worship_id || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          <select
                            className="border border-gray-300 rounded px-2 py-1"
                            value={enrollment.status || ''}
                            onChange={async (e) => {
                              const newStatus = e.target.value || null;
                              try {
                                setSaving(true);
                                const updated = await adminApiService.events.updateEnrollmentStatus(event.id, sectionId, enrollment.member_id, newStatus);
                                setEnrollments(prev => prev.map(en => en.id === enrollment.id ? { ...en, status: updated.status } : en));
                                dispatch(addNotification({ type: 'success', message: '已更新出席狀態' }));
                              } catch (err) {
                                console.error('Update status failed', err);
                                dispatch(addNotification({ type: 'error', message: '更新出席狀態失敗' }));
                              } finally {
                                setSaving(false);
                              }
                            }}
                            disabled={saving}
                          >
                            <option value="">無</option>
                            <option value="excused">告假</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => handleRemove(enrollment.member_id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                            disabled={saving}
                          >
                            移除
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseEnrollmentModal;
